import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { ADMIN_TOOLS, executeAdminToolCall } from '../../../lib/ai-tools-admin';

interface AdminChatRequest {
  mensaje: string;
  historial?: { rol: string; contenido: string }[];
}

interface ToolCallInfo {
  name: string;
  args: Record<string, unknown>;
  result: { success: boolean; data?: unknown; error?: string };
}

interface AdminChatResponse {
  success: boolean;
  respuesta?: string;
  fuentes?: string[];
  toolCalls?: ToolCallInfo[];
  error?: string;
}

const MAX_TOOL_ITERATIONS = 5;

/**
 * POST /api/assistant/admin-chat
 * Admin assistant with OpenAI function calling.
 * Uses ADMIN_TOOLS (asesor tools + 5 admin-specific tools) for real DB queries.
 * No mocked values. No keyword matching.
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse<AdminChatResponse>,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'POST') {
    return res.status(405).json({ success: false, error: 'Method not allowed' });
  }

  const { mensaje, historial } = req.body as AdminChatRequest;

  if (!mensaje || typeof mensaje !== 'string') {
    return res.status(400).json({ success: false, error: 'Mensaje requerido.' });
  }

  const openaiApiKey = process.env.OPENAI_API_KEY;

  // ── No API key: call estadisticas_sistema directly and return formatted response ──
  if (!openaiApiKey) {
    try {
      const stats = await executeAdminToolCall('estadisticas_sistema', {}, {
        userId: user.id,
        userRole: 'admin',
      });
      const d = stats.success ? (stats.data as Record<string, unknown>) : null;
      const respuesta = d
        ? `Soy E2-Admin. Aquí tienes el estado actual del sistema:\n\n` +
          `👥 **Clientes:** ${(d.clientes as any)?.activos} activos de ${(d.clientes as any)?.total}\n` +
          `📄 **Documentos este mes:** ${(d.documentos as any)?.total_este_mes} (${(d.documentos as any)?.tasa_exito_mes} procesados)\n` +
          `💬 **Incidencias abiertas:** ${(d.incidencias as any)?.abiertas}\n` +
          `👤 **Asesores:** ${(d.asesores as any)?.total}\n` +
          `🔔 **Alertas pendientes:** ${d.alertas_pendientes}\n\n` +
          `¿En qué más puedo ayudarte?`
        : 'Estoy aquí para ayudarte. Sin clave de OpenAI configurada las respuestas son limitadas. ¿Qué necesitas?';
      return res.status(200).json({ success: true, respuesta, fuentes: ['DB real'] });
    } catch {
      return res.status(200).json({
        success: true,
        respuesta: 'El asistente no está disponible sin clave de OpenAI configurada.',
        fuentes: [],
      });
    }
  }

  // ── Build conversation messages ───────────────────────────────
  const messages: Array<{
    role: string;
    content?: string | null;
    tool_calls?: unknown[];
    tool_call_id?: string;
  }> = [
    {
      role: 'system',
      content: `Eres E2-Admin, el asistente de gestión para el equipo administrativo de Externaliza2, una asesoría contable española.

CAPACIDADES CON HERRAMIENTAS:
Tienes acceso a herramientas que consultan datos REALES del sistema en tiempo real:
- estadisticas_sistema — métricas globales: clientes, documentos, incidencias, alertas, sync
- listar_asesores — equipo de asesores con carga de clientes
- estado_sync_cegid — historial de sincronizaciones con el ERP Cegid
- listar_alertas_sistema — alertas activas y pendientes de revisión
- resumen_financiero_global — ingresos, gastos y resultados de la cartera de clientes
- listar_clientes, listar_incidencias, buscar_documentos — herramientas compartidas con asesores

REGLAS CRÍTICAS:
- SIEMPRE responde en español
- Cuando el usuario pida datos concretos, USA las herramientas — NUNCA inventes números
- Cuando obtengas resultados de una herramienta, preséntelos de forma clara con markdown
- Si una herramienta falla, explícalo y sugiere alternativas
- Si no tienes datos para algo específico, indícalo claramente
- Usa lenguaje técnico pero claro. Sé conciso y directo.`,
    },
  ];

  // Add conversation history (last 8 messages)
  if (historial && historial.length > 0) {
    historial.slice(-8).forEach((msg) => {
      messages.push({
        role: msg.rol === 'usuario' ? 'user' : 'assistant',
        content: msg.contenido,
      });
    });
  }

  // Add current question
  messages.push({ role: 'user', content: mensaje });

  // Tool execution context
  const toolContext = { userId: user.id, userRole: 'admin' as const };
  const allToolCalls: ToolCallInfo[] = [];

  // ── Function Calling Loop ─────────────────────────────────────
  let finalResponse = '';

  for (let iteration = 0; iteration < MAX_TOOL_ITERATIONS; iteration++) {
    const openaiResponse = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${openaiApiKey}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages,
        tools: ADMIN_TOOLS,
        tool_choice: 'auto',
        temperature: 0.3,
        max_tokens: 3000,
      }),
    });

    if (!openaiResponse.ok) {
      const error = await openaiResponse.text();
      console.error('OpenAI API error (admin):', error);
      throw new Error('Error al comunicarse con OpenAI');
    }

    const openaiData = await openaiResponse.json();
    const choice = openaiData.choices[0];
    const assistantMessage = choice.message;

    // If the model wants to call tools
    if (choice.finish_reason === 'tool_calls' && assistantMessage.tool_calls) {
      // Add the assistant message with tool_calls to history
      messages.push({
        role: 'assistant',
        content: assistantMessage.content || null,
        tool_calls: assistantMessage.tool_calls,
      });

      // Execute each tool call
      for (const toolCall of assistantMessage.tool_calls) {
        const fnName = toolCall.function.name as string;
        let fnArgs: Record<string, unknown> = {};
        try {
          fnArgs = JSON.parse(toolCall.function.arguments);
        } catch {
          fnArgs = {};
        }

        console.log(`[AI Admin Tool] Executing: ${fnName}`, fnArgs);
        const result = await executeAdminToolCall(fnName, fnArgs, toolContext);

        allToolCalls.push({ name: fnName, args: fnArgs, result });

        // Add tool result to message history
        messages.push({
          role: 'tool',
          tool_call_id: toolCall.id,
          content: JSON.stringify(result),
        });
      }

      // Continue the loop — OpenAI will process the tool results
      continue;
    }

    // Model returned a final text response
    finalResponse = assistantMessage.content || '';
    break;
  }

  // Determine fuentes based on tools used
  const fuentes = ['Normativa fiscal vigente'];
  if (allToolCalls.length > 0) {
    fuentes.unshift('Datos del sistema Externaliza2');
  }

  return res.status(200).json({
    success: true,
    respuesta: finalResponse,
    fuentes,
    toolCalls: allToolCalls.length > 0 ? allToolCalls : undefined,
  });
}

// Wrap error handling around the entire handler
async function wrappedHandler(
  req: NextApiRequest,
  res: NextApiResponse<AdminChatResponse>,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  try {
    return await handler(req, res, user);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Error interno del servidor';
    console.error('Admin assistant chat error:', message);
    return res.status(500).json({ success: false, error: message });
  }
}

export default withAuth(wrappedHandler, ['admin']);
